import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, switchMap, tap, map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type WizardVersion = 'v1' | 'v2';

export type YesNoMaybe = 'yes' | 'no' | 'maybe';

export interface WizardData {
  wizardVersion?: WizardVersion;
  firstName?: string;
  lastName?: string;
  subjects?: string[];
  grades?: string[];
  school?: string;
  district?: string;
  state?: string;
  role?: string;
  challenges?: string[];
  aiHelpDescription?: string;
  usePlatform?: YesNoMaybe;
  usePlatformReason?: string;
}

export interface WizardDataResponse {
  success: boolean;
  data: WizardData;
}

@Injectable({
  providedIn: 'root'
})
export class WizardDataService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  private readonly data$ = new BehaviorSubject<WizardData>({});
  private readonly saveRequest$ = new Subject<WizardData>();
  private readonly isSaving$ = new BehaviorSubject<boolean>(false);
  private readonly saveError$ = new BehaviorSubject<string | null>(null);
  private readonly isLoaded$ = new BehaviorSubject<boolean>(false);
  private loadPromise: Promise<WizardData> | null = null;

  constructor() {
    this.initAutoSave();
  }

  /**
   * Check if data has been loaded from backend
   */
  isLoaded(): boolean {
    return this.isLoaded$.value;
  }

  /**
   * Ensure data is loaded from backend (loads only once)
   * Returns a promise that resolves when data is ready
   */
  ensureLoaded(): Promise<WizardData> {
    if (this.isLoaded$.value) {
      return Promise.resolve(this.data$.value);
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      this.fetchFromApi().subscribe({
        next: (data) => {
          this.data$.next(data);
          this.isLoaded$.next(true);
          resolve(data);
        },
        error: (error) => {
          // Even on error, mark as loaded to prevent infinite retries
          this.isLoaded$.next(true);
          resolve({});
        }
      });
    });

    return this.loadPromise;
  }

  /**
   * Get the current wizard data as an observable
   */
  getData(): Observable<WizardData> {
    return this.data$.asObservable();
  }

  /**
   * Get the current wizard data value synchronously
   */
  getDataSnapshot(): WizardData {
    return this.data$.value;
  }

  /**
   * Get saving state as an observable
   */
  isSaving(): Observable<boolean> {
    return this.isSaving$.asObservable();
  }

  /**
   * Get save error as an observable
   */
  getSaveError(): Observable<string | null> {
    return this.saveError$.asObservable();
  }

  /**
   * Save partial wizard data - merges with existing data and triggers auto-save
   * Call this on every input change
   */
  save(data: Partial<WizardData>): void {
    const currentData = this.data$.value;
    const mergedData = this.mergeData(currentData, data);
    this.data$.next(mergedData);
    this.saveRequest$.next(mergedData);
  }

  /**
   * Reset in-memory wizard data without affecting backend.
   * Should be called on user logout to ensure fresh state for next user.
   */
  reset(): void {
    this.data$.next({});
    this.isLoaded$.next(false);
    this.loadPromise = null;
    this.isSaving$.next(false);
    this.saveError$.next(null);
  }

  /**
   * Initialize the auto-save mechanism with debouncing
   */
  private initAutoSave(): void {
    this.saveRequest$
      .pipe(
        debounceTime(500),
        tap(() => {
          this.isSaving$.next(true);
          this.saveError$.next(null);
        }),
        switchMap(data =>
          this.saveToApi(data).pipe(
            catchError(error => {
              this.saveError$.next(error.message || 'Failed to save wizard data');
              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: () => {
          this.isSaving$.next(false);
        }
      });
  }

  /**
   * Merge new data with existing data, handling arrays properly
   */
  private mergeData(current: WizardData, updates: Partial<WizardData>): WizardData {
    return {
      ...current,
      ...updates
    };
  }

  /**
   * Save data to the API
   */
  private saveToApi(data: WizardData): Observable<WizardDataResponse> {
    return this.http.post<WizardDataResponse>(`${this.apiUrl}/wizard/data`, data);
  }

  /**
   * Fetch data from the API
   */
  private fetchFromApi(): Observable<WizardData> {
    return this.http.get<WizardDataResponse>(`${this.apiUrl}/wizard/data`).pipe(
      map(response => response.data)
    );
  }
}

